import React from 'react';
import SheetImposition from '@/components/SheetImposition';
import '@/components/SheetImposition.css';

const formatNumber = (value) => Number(value || 0).toLocaleString('es-AR');

const formatSize = (width, height, unit = 'cm') => {
  if (!width && !height) return '-';
  return `${formatNumber(width)} x ${formatNumber(height)} ${unit}`;
};

export const getProductionImpositionGroups = (order) => {
  if (Array.isArray(order?.imposition_groups) && order.imposition_groups.length > 0) {
    return order.imposition_groups;
  }
  if (!order?.imposition?.label) return [];
  return [{
    key: 'principal',
    title: 'Elemento Numero 1',
    subtitle: 'Trabajo',
    kind: 'simple',
    item_width: order.imposition?.pieces?.[0]?.w || 0,
    item_height: order.imposition?.pieces?.[0]?.h || 0,
    sheets: order.imposition?.sheets_needed,
    mother_sheets: order.imposition?.mother_sheets_needed,
    items_per_sheet: order.imposition?.items_per_sheet,
    imposition: order.imposition,
  }];
};

export const ProductionImpositionGroup = ({ group, index, large = false }) => {
  const imposition = group.imposition || {};
  const config = imposition.config || {};
  const pieces = imposition.pieces || [];
  const itemWidth = Number(group.item_width || pieces[0]?.w || 0);
  const itemHeight = Number(group.item_height || pieces[0]?.h || 0);
  const sourceSheet = imposition.source_sheet || {};
  const generatedSheet = imposition.generated_sheet || {};
  const sheetWidth = group.sheet_width || sourceSheet.width;
  const sheetHeight = group.sheet_height || sourceSheet.height;
  const generatedWidth = group.generated_width || generatedSheet.width || config.sheetWidth;
  const generatedHeight = group.generated_height || generatedSheet.height || config.sheetHeight;
  const previewWidth = large ? 280 : 240;

  return (
    <div className="production-work-sheet" data-testid={`production-imposition-group-${group.key || index}`}>
      <div className="production-work-sheet-header">
        <strong>{group.title || `Elemento Numero ${index + 1}`}</strong>
        <span>{group.subtitle || group.kind || 'Trabajo'}</span>
      </div>

      <div className="production-work-sheet-grid">
        <div className="production-work-sheet-panel">
          <div className="production-work-row"><strong>Material:</strong> <span>{group.paper || '-'}</span></div>
          {group.grammage ? <div className="production-work-row"><strong>Gramaje:</strong> <span>{formatNumber(group.grammage)} gr</span></div> : null}
          {group.mother_sheets ? <div className="production-work-row"><strong>Hojas:</strong> <span>{formatNumber(group.mother_sheets)}</span></div> : null}
          {group.spoilage_mother_sheets ? <div className="production-work-row"><strong>Desperdicio:</strong> <span>{formatNumber(group.spoilage_mother_sheets)} hojas</span></div> : null}
          <div className="production-work-row"><strong>Medidas:</strong> <span>{formatSize(itemWidth, itemHeight)}</span></div>
          <div className="production-work-row"><strong>Detalle:</strong> <span>{group.subtitle || '-'}</span></div>

          <div className="production-work-diagram-label">Diagrama de Hoja</div>
          <div className="production-sheet-outline">
            <span className="production-sheet-size left">{formatNumber(sheetHeight || config.sourceCut?.sourceSheet?.height || 0)}</span>
            <div className="production-sheet-box">
              <div></div>
              <div></div>
            </div>
            <span className="production-sheet-size bottom">{formatNumber(sheetWidth || config.sourceCut?.sourceSheet?.width || 0)}</span>
          </div>
        </div>

        <div className="production-work-sheet-panel">
          <div className="production-work-row"><strong>Formato Hoja:</strong> <span>{formatSize(sheetWidth, sheetHeight)}</span></div>
          <div className="production-work-row"><strong>Formato Pliego:</strong> <span>{formatSize(generatedWidth, generatedHeight)}</span></div>
          <div className="production-work-row"><strong>Boc/Pie/Pag:</strong> <span>{formatNumber(group.items_per_sheet || imposition.items_per_sheet || 0)}</span></div>
          <div className="production-work-row"><strong>Cantidad Pliegos:</strong> <span>{formatNumber(group.sheets || imposition.sheets_needed || 0)}</span></div>
          {group.pages ? <div className="production-work-row"><strong>Paginas:</strong> <span>{formatNumber(group.pages)}</span></div> : null}
          {group.pages_per_sheet ? <div className="production-work-row"><strong>Paginas por pliego:</strong> <span>{formatNumber(group.pages_per_sheet)}</span></div> : null}
          <div className="production-work-mini-title">Impresion:</div>
          <div className="production-work-row"><strong>Planchas:</strong> <span>{formatNumber(group.plates || 0)}</span></div>
          <div className="production-work-row"><strong>Colores:</strong> <span>{group.colors || '-'}</span></div>
          <div className="production-work-row"><strong>Maquina:</strong> <span>Offset principal</span></div>
        </div>

        <div className="production-work-sheet-panel production-work-diagram-panel">
          <div className="production-work-diagram-label">Diagrama de Pliego</div>
          {config && pieces.length > 0 ? (
            <div className="production-work-imposition-preview">
              <SheetImposition
                sheetWidth={config.sheetWidth}
                sheetHeight={config.sheetHeight}
                gripperTop={config.gripperTop}
                gripperBottom={config.gripperBottom}
                marginLeft={config.marginLeft}
                marginRight={config.marginRight}
                pieces={pieces}
                itemWidth={itemWidth}
                itemHeight={itemHeight}
                bleed={config.bleed}
                side="front"
                maxWidth={previewWidth}
                showLegend={false}
              />
              {config.doubleSided && (
                <SheetImposition
                  sheetWidth={config.sheetWidth}
                  sheetHeight={config.sheetHeight}
                  gripperTop={config.gripperTop}
                  gripperBottom={config.gripperBottom}
                  marginLeft={config.marginLeft}
                  marginRight={config.marginRight}
                  pieces={pieces}
                  itemWidth={itemWidth}
                  itemHeight={itemHeight}
                  bleed={config.bleed}
                  side="back"
                  maxWidth={previewWidth}
                  showLegend={false}
                />
              )}
            </div>
          ) : (
            <div className="empty-state compact-empty">Sin diagrama</div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductionWorkSheets = ({ order, large = false }) => {
  const groups = getProductionImpositionGroups(order);

  if (groups.length === 0) {
    return <div className="empty-state compact-empty">Esta orden no tiene datos de imposicion guardados.</div>;
  }

  return (
    <div className="production-work-sheets">
      {groups.map((group, index) => (
        <ProductionImpositionGroup
          key={group.key || `${group.title}-${index}`}
          group={group}
          index={index}
          large={large}
        />
      ))}
    </div>
  );
};

export default ProductionWorkSheets;
